import { useEffect } from 'react'
import { CoachMark } from '@/components'
import { backtestCoachStore, useBacktestCoach } from '../backtestCoach'

/**
 * First-backtest coach sequence (see backtestCoach.ts for the state machine).
 * Step 1 spotlights the results badge in chat; step 2 the "Overlay backtest"
 * checkbox on the workbench toolbar. Non-blocking throughout — the app stays
 * fully usable underneath.
 */

const CARD_COPY =
  "Your backtest is complete — here's where you'll see the results. From here you can run the optimizer or open the full report."
const TOGGLE_COPY =
  'Your results are also overlaid on the chart. Use this toggle to show or hide them anytime.'

// The whole label row around the checkbox (shared by the desktop and mobile
// toolbar variants — only one is mounted at a time).
const TOGGLE_TARGET = 'label[for="backtest-orders-btn"]'
// How long step 2 waits for the toolbar to exist (a tab switch needs a render
// or two) before concluding the user is elsewhere and ending the sequence.
const TOGGLE_GRACE_MS = 1200

interface BacktestCoachProps {
  isMobile: boolean
  /** Step-1 ack on mobile closes the chat sheet so the toolbar is visible for step 2. */
  onCloseMobileChat: () => void
}

export default function BacktestCoach({ isMobile, onCloseMobileChat }: BacktestCoachProps) {
  const { step, msgId } = useBacktestCoach()

  // The overlay toggle only exists on the Create tab — if the user is
  // elsewhere when step 2 comes up, finish (and mark seen) instead of leaving
  // the sequence stranded. Step 1 deliberately has no such timeout: the badge
  // being hidden is transient chat visibility, and the spotlight simply
  // appears when it mounts.
  useEffect(() => {
    if (step !== 'toggle') return undefined
    const timer = setTimeout(() => {
      if (!document.querySelector(TOGGLE_TARGET)) backtestCoachStore.next()
    }, TOGGLE_GRACE_MS)
    return () => clearTimeout(timer)
  }, [step])

  if (step === 'card' && msgId) {
    const ack = () => {
      if (isMobile) onCloseMobileChat()
      backtestCoachStore.next()
    }
    return (
      <CoachMark
        targetSelector={`[data-coach="backtest-card"][data-msg-id="${msgId}"]`}
        placement={isMobile ? 'top' : 'left'}
        copy={CARD_COPY}
        primaryLabel="Got it"
        onPrimary={ack}
        onDismiss={ack}
      />
    )
  }

  if (step === 'toggle') {
    const ack = () => backtestCoachStore.next()
    return (
      <CoachMark
        targetSelector={TOGGLE_TARGET}
        placement="bottom"
        copy={TOGGLE_COPY}
        primaryLabel="OK"
        onPrimary={ack}
        onDismiss={ack}
      />
    )
  }

  return null
}
